import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  Bell,
  CheckCheck,
  MessageSquare,
  ListTodo,
  Clock,
  Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type Notification,
} from "@/hooks/useNotifications";
import { useInvoice, useInvoiceReminder } from "@/hooks/useInvoices";
import { useRightSidebar } from "@/contexts/RightSidebarContext";
import {
  requestNotificationPermission,
  getNotificationPermission,
} from "@/lib/desktop-notifications";
import { cn } from "@/lib/utils";
import { useState } from "react";

function getEntityRoute(notification: Notification): string | null {
  const entityType = notification.entity_type;
  const entityId = notification.entity_id;
  if (!entityType || !entityId) return null;
  const routes: Record<string, string> = {
    project: `/projects/${entityId}`,
    company: `/companies/${entityId}`,
    person: `/people/${entityId}`,
    equipment: `/equipment/${entityId}`,
    meeting_note: `/meeting-notes/${entityId}`,
    project_group: `/project-groups/${entityId}`,
    task: `/tasks?task=${encodeURIComponent(
      notification.source_task_id ?? entityId,
    )}`,
  };
  return routes[entityType] || null;
}

function NotificationIcon({ type }: { type: Notification["type"] }) {
  switch (type) {
    case "comment_mention":
    case "meeting_note_mention":
      return <MessageSquare className="h-4 w-4 text-blue-500" />;
    case "task_assigned":
    case "task_updated":
      return <ListTodo className="h-4 w-4 text-green-500" />;
    case "task_due_soon":
      return <Clock className="h-4 w-4 text-amber-500" />;
    case "task_due_today":
      return <Clock className="h-4 w-4 text-red-500" />;
    case "invoice_payment_overdue":
      return <Receipt className="h-4 w-4 text-amber-500" />;
    default:
      return <Bell className="h-4 w-4" />;
  }
}

function NotificationItem({ notification }: { notification: Notification }) {
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();
  const { closeSidebar } = useRightSidebar();
  const { data: invoice } = useInvoice(
    notification.entity_type === "invoice"
      ? (notification.entity_id ?? undefined)
      : undefined,
  );
  const { data: reminder } = useInvoiceReminder(
    notification.entity_type === "invoice_reminder"
      ? notification.entity_id
      : null,
  );

  const handleClick = () => {
    if (!notification.is_read) {
      markRead.mutate(notification.id);
    }
    const route =
      invoice?.project_id && notification.entity_id
        ? `/projects/${invoice.project_id}/finance/invoices/${notification.entity_id}`
        : reminder?.invoice?.project_id
          ? `/projects/${reminder.invoice.project_id}/finance/invoices/${reminder.invoice_id}`
          : getEntityRoute(notification);
    if (route) {
      closeSidebar();
      navigate(route);
    }
  };

  return (
    <button
      className={cn(
        "flex gap-2.5 p-2.5 w-full text-left rounded-md hover:bg-muted transition-colors",
        !notification.is_read && "bg-muted/50",
      )}
      onClick={handleClick}
    >
      <div className="mt-0.5">
        <NotificationIcon type={notification.type} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p
            className={cn(
              "text-sm truncate",
              !notification.is_read && "font-medium",
            )}
          >
            {notification.title}
          </p>
          {!notification.is_read && (
            <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
          )}
        </div>
        {notification.body && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {notification.body}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          {formatDistanceToNow(new Date(notification.created_at), {
            addSuffix: true,
          })}
        </p>
      </div>
    </button>
  );
}

export function NotificationsPanel() {
  const { data: notifications, isLoading, unreadCount } = useNotifications();
  const markAllRead = useMarkAllNotificationsRead();
  const [permissionState, setPermissionState] = useState(
    getNotificationPermission,
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
          >
            <CheckCheck className="h-3.5 w-3.5 mr-1" />
            Mark all read
          </Button>
        )}
        <div />
      </div>

      {permissionState !== "granted" && permissionState !== "denied" && (
        <div className="px-3 py-2 border-b bg-muted/50">
          <p className="text-xs text-muted-foreground mb-1.5">
            Enable desktop notifications to stay informed when you're not on
            this tab.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7"
            onClick={async () => {
              const result = await requestNotificationPermission();
              setPermissionState(result);
            }}
          >
            <Bell className="h-3 w-3 mr-1" />
            Enable
          </Button>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-0.5">
          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Loading...
            </p>
          ) : !notifications?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <Bell className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                No notifications yet
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
